import { useId, useRef } from 'react'
import { useBodyScrollLock, useModalFocusTrap } from '../hooks/useModalFocusTrap'
import { XCloseIcon } from './ui/icons'
import { PrimaryButton } from './ui'

export interface ClaimAdditionalExcessesModalProps {
  open: boolean
  onClose: () => void
  /** Shown in water-crossing line when known (e.g. "$45,000"). */
  sumInsuredSnippet: string
}

/**
 * Education modal listing types of additional excess that may apply (PDS-style summary).
 * Mobile: full-width sheet from bottom with scroll; desktop: centered card with max width.
 */
export default function ClaimAdditionalExcessesModal({
  open,
  onClose,
  sumInsuredSnippet,
}: ClaimAdditionalExcessesModalProps) {
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)

  useModalFocusTrap({ active: open, containerRef: dialogRef, onClose })
  useBodyScrollLock(open)

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-4"
      role="presentation"
    >
      <button
        type="button"
        className="absolute inset-0 cursor-default border-0 bg-background-overlay/60 p-0 backdrop-blur-[2px]"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative flex max-h-[min(92dvh,880px)] w-full max-w-[560px] flex-col rounded-t-2xl border border-border-primary bg-background-primary shadow-lg outline-none sm:max-h-[min(85vh,720px)] sm:rounded-2xl"
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border-secondary px-4 py-4 sm:px-6">
          <div className="flex min-w-0 items-start gap-3">
            <span
              className="flex size-10 shrink-0 items-center justify-center rounded-full bg-foreground-brand-primary-alt text-text-primary-900"
              aria-hidden
            >
              <span className="font-body text-lg font-bold leading-none">i</span>
            </span>
            <h2 id={titleId} className="m-0 pt-1 font-display text-lg font-semibold text-text-primary-900 sm:text-xl">
              Additional excesses
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex size-10 shrink-0 items-center justify-center rounded-md text-text-secondary-700 hover:bg-background-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand-solid"
            aria-label="Close"
          >
            <XCloseIcon className="size-6" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-5">
          <p className="m-0 font-body text-sm leading-sm text-text-secondary-700">
            When you make a claim, you may be required to pay one or more additional excesses on top of your standard
            excess. The additional excess(es) applicable to your claim will depend on the specific circumstances
            involved. The types of additional excesses that may apply include:
          </p>

          <ul className="mt-4 flex list-none flex-col gap-0 p-0">
            <li className="border-b border-border-secondary py-4 first:pt-2">
              <p className="m-0 font-body font-semibold text-sm text-text-primary-900">Unlisted driver excess</p>
              <p className="mt-1 m-0 font-body text-sm font-semibold text-text-primary-900">$1,000</p>
              <p className="mt-2 m-0 font-body text-sm leading-sm text-text-secondary-700">
                Payable if the person driving the vehicle at the time of the event is:
              </p>
              <ul className="mt-2 mb-0 list-disc pl-5 font-body text-sm leading-sm text-text-secondary-700">
                <li>Under 25; and</li>
                <li>Not listed on your policy.</li>
              </ul>
            </li>
            <li className="border-b border-border-secondary py-4">
              <p className="m-0 font-body font-semibold text-sm text-text-primary-900">Inexperienced driver excess</p>
              <p className="mt-1 m-0 font-body text-sm font-semibold text-text-primary-900">$1,000</p>
              <p className="mt-2 m-0 font-body text-sm leading-sm text-text-secondary-700">
                Payable if the person driving the vehicle at the time of the event is:
              </p>
              <ul className="mt-2 mb-0 list-disc pl-5 font-body text-sm leading-sm text-text-secondary-700">
                <li>Under 25; or</li>
                <li>Has not held an Australian driver licence for more than 2 years; or</li>
                <li>Holds a learner licence or permit.</li>
              </ul>
              <p className="mt-3 m-0 font-body text-sm leading-sm text-text-tertiary-600">
                This won&apos;t apply to claims involving fire, theft and malicious damage,{' '}
                <strong className="font-semibold text-text-secondary-700">broken windscreen</strong> or damage
                sustained while your car is parked.
              </p>
            </li>
            <li className="py-4">
              <p className="m-0 font-body font-semibold text-sm text-text-primary-900">Water crossing excess</p>
              <p className="mt-1 m-0 font-body text-sm font-semibold text-text-primary-900">
                5% of the total sum insured ({sumInsuredSnippet}), minimum $2,000
              </p>
              <p className="mt-2 m-0 font-body text-sm leading-sm text-text-secondary-700">
                Payable if loss or damage arises from the vehicle being driven through any man-made or natural body of
                water.
              </p>
            </li>
          </ul>
        </div>

        <div className="shrink-0 border-t border-border-secondary bg-background-primary px-4 py-4 sm:px-6">
          <PrimaryButton type="button" fullWidth size="lg" onClick={onClose}>
            Continue
          </PrimaryButton>
        </div>
      </div>
    </div>
  )
}
